import React, { useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

// Type definitions
interface AdvertisingOption {
  title: string;
  description: string;
  price: string;
  features: string[];
  popular?: boolean;
}

interface AudienceSegment {
  category: string;
  percentage: number;
  description: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface Testimonial {
  quote: string;
  author: string;
  company: string;
  avatar: string;
}

// Constants outside component to prevent recreation
const STATS = [
  { number: '50K+', label: 'Monthly Visitors' },
  { number: '15K+', label: 'AI Enthusiasts' },
  { number: '5K+', label: 'Developers' },
  { number: '2K+', label: 'Business Owners' }
] as const;

const ADVERTISING_OPTIONS: AdvertisingOption[] = [
  {
    title: 'Sponsored Listing',
    description: 'Feature your tool at the top of relevant categories',
    price: '$299/month',
    features: [
      'Premium placement in category pages',
      'Featured badge on your listing',
      'Higher click-through rates',
      '30-day minimum commitment'
    ],
    popular: true
  },
  {
    title: 'Homepage Banner',
    description: 'Prominent visibility on our homepage',
    price: '$499/month',
    features: [
      'Rotating banner on homepage',
      'Guaranteed impressions',
      'Brand awareness focus',
      'Custom creative support'
    ]
  },
  {
    title: 'Newsletter Feature',
    description: 'Reach our engaged email subscribers',
    price: '$199/edition',
    features: [
      'Featured in weekly newsletter',
      '50K+ subscriber reach',
      'Dedicated section',
      'Performance analytics'
    ]
  }
];

const AUDIENCE_DATA: AudienceSegment[] = [
  {
    category: 'Developers & Engineers',
    percentage: 35,
    description: 'Tech-savvy professionals building AI solutions'
  },
  {
    category: 'Business Owners',
    percentage: 25,
    description: 'Decision-makers seeking AI tools for their business'
  },
  {
    category: 'AI Researchers',
    percentage: 20,
    description: 'Academics and researchers in AI field'
  },
  {
    category: 'Content Creators',
    percentage: 15,
    description: 'Creators using AI for content generation'
  },
  {
    category: 'Students & Learners',
    percentage: 5,
    description: 'Individuals learning about AI technologies'
  }
];

const TESTIMONIALS: Testimonial[] = [
  {
    quote: "Advertising on TooliQ.ai helped us reach the right audience and increased our signups by 300% in the first month.",
    author: "Sarah Chen",
    company: "AI Assistant Pro",
    avatar: "👩‍💼"
  },
  {
    quote: "The quality of traffic from TooliQ.ai is exceptional. Our conversion rate is 5x higher than other platforms.",
    author: "Marcus Johnson",
    company: "CodeGenius",
    avatar: "👨‍💻"
  },
  {
    quote: "As a startup, every marketing dollar counts. TooliQ.ai delivered the best ROI we've seen from any channel.",
    author: "Elena Rodriguez",
    company: "Artify AI",
    avatar: "👩‍🎨"
  }
];

const FAQ_ITEMS: FAQItem[] = [
  {
    question: "What is the minimum advertising commitment?",
    answer: "Most of our advertising options require a 30-day minimum commitment to ensure optimal results. However, we do offer some flexible options for seasonal campaigns."
  },
  {
    question: "How do you track advertising performance?",
    answer: "We provide detailed analytics including impressions, clicks, CTR, and conversions. You'll get access to a dedicated dashboard with real-time performance metrics."
  },
  {
    question: "Can I target specific audience segments?",
    answer: "Yes! We offer advanced targeting options including by geography, industry, job role, and technology interests to ensure your ads reach the most relevant audience."
  },
  {
    question: "What ad formats do you support?",
    answer: "We support various formats including display banners, sponsored listings, native content, and newsletter features. Our team can help you choose the best format for your goals."
  },
  {
    question: "How quickly can my campaign start?",
    answer: "Once approved, campaigns can typically launch within 24-48 hours. We recommend submitting your materials at least 3 business days before your desired start date."
  },
  {
    question: "Do you offer discounts for long-term commitments?",
    answer: "Yes, we offer volume discounts for 3, 6, and 12-month commitments. Contact our sales team to discuss custom pricing for longer campaigns."
  }
];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'audience', label: 'Our Audience' },
  { id: 'pricing', label: 'Advertising Options' },
  { id: 'faq', label: 'FAQ' },
  { id: 'contact', label: 'Get Started' }
] as const;

type TabType = typeof TABS[number]['id'];

const AdvertisePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [activeFAQ, setActiveFAQ] = useState<number | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // SEO data - memoized
  const seoData = useMemo(() => ({
    title: 'Advertise on TooliQ.ai | Reach 50,000+ AI Enthusiasts & Developers',
    description: 'Promote your AI tools to 50,000+ monthly visitors on TooliQ.ai. Targeted advertising for AI companies. Reach developers, businesses & decision-makers.',
    keywords: 'advertise AI tools, AI tool advertising, promote AI software, AI developer marketing, AI tool sponsorship, AI product marketing, reach AI enthusiasts',
    canonical: 'https://tooliq.ai/advertise'
  }), []);

  // Event handlers - useCallback for performance
  const handleContactSales = useCallback(() => {
    setActiveTab('contact');
    setTimeout(() => {
      document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleViewMediaKit = useCallback(() => {
    alert('Media kit download started! This would typically download a PDF with advertising specifications.');
  }, []);

  const handleSelectPlan = useCallback((planName: string) => {
    setActiveTab('contact');
    setTimeout(() => {
      document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleScheduleCall = useCallback(() => {
    window.open('https://calendly.com/tooliq-ai/advertising-consultation', '_blank');
  }, []);

  const toggleFAQ = useCallback((index: number) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  }, [activeFAQ]);

  const handleSubmitForm = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      document.getElementById('form-success')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleTabChange = useCallback((tabId: TabType) => {
    setActiveTab(tabId);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://tooliq.ai/og-images/advertise.webp" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content="https://tooliq.ai/og-images/advertise.webp" />

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Advertise on TooliQ.ai",
              "description": seoData.description,
              "url": seoData.canonical,
              "publisher": {
                "@type": "Organization",
                "name": "TooliQ.ai",
                "url": "https://tooliq.ai"
              }
            })
          }}
        />
      </Helmet>

      {/* Hero Section */}
      <HeroSection 
        onContactSales={handleContactSales}
        onViewMediaKit={handleViewMediaKit}
      />

      {/* Stats Section */}
      <StatsSection />

      {/* Tabs Navigation */}
      <TabsNavigation activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <TabContent 
          activeTab={activeTab}
          activeFAQ={activeFAQ}
          formSubmitted={formSubmitted}
          onSelectPlan={handleSelectPlan}
          onToggleFAQ={toggleFAQ}
          onSubmitForm={handleSubmitForm}
          onScheduleCall={handleScheduleCall}
          onContactSales={handleContactSales}
        />
      </div>

      {/* Final CTA Section */}
      <FinalCTASection 
        onContactSales={handleContactSales}
        onScheduleCall={handleScheduleCall}
      />
    </div>
  );
};

// Extracted HeroSection component
interface HeroSectionProps {
  onContactSales: () => void;
  onViewMediaKit: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onContactSales, onViewMediaKit }) => (
  <section className="relative py-20 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto text-center">
      <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 text-sm font-medium mb-6">
        🚀 Reach 50,000+ AI Enthusiasts Monthly
      </div>
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
        Advertise Your AI Tool
        <span className="text-blue-600 block">Reach the Right Audience</span>
      </h1>
      
      {/* Hero SEO Content */}
      <div className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-gray-200/50">
        <p className="text-lg text-gray-700 leading-relaxed">
          <strong>Promote your AI tools</strong> to our engaged audience of <strong>50,000+ monthly visitors</strong> including <strong>developers, businesses, 
          and AI enthusiasts</strong>. Get featured on the fastest-growing AI tools directory and connect with customers actively searching for solutions like yours.
        </p>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button 
          onClick={onContactSales}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Start Advertising Today
        </button>
        <button 
          onClick={onViewMediaKit}
          className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:border-blue-500 hover:text-blue-600 transition-colors hover:shadow-md"
        >
          View Media Kit
        </button>
      </div>
    </div>
  </section>
);

// Extracted StatsSection component
const StatsSection: React.FC = () => (
  <section className="py-16 bg-white/50 backdrop-blur-sm">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Audience Reach & Engagement</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Connect with a highly targeted audience actively searching for AI solutions
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {STATS.map((stat, index) => (
          <div key={index} className="text-center bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              {stat.number}
            </div>
            <div className="text-gray-700 font-medium">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Extracted TabsNavigation component
interface TabsNavigationProps {
  activeTab: TabType;
  onTabChange: (tabId: TabType) => void;
}

const TabsNavigation: React.FC<TabsNavigationProps> = ({ activeTab, onTabChange }) => (
  <section className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`px-6 py-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  </section>
);

// Extracted TabContent component
interface TabContentProps {
  activeTab: TabType;
  activeFAQ: number | null;
  formSubmitted: boolean;
  onSelectPlan: (planName: string) => void;
  onToggleFAQ: (index: number) => void;
  onSubmitForm: (e: React.FormEvent) => void;
  onScheduleCall: () => void;
  onContactSales: () => void;
}

const TabContent: React.FC<TabContentProps> = ({
  activeTab,
  activeFAQ,
  formSubmitted,
  onSelectPlan,
  onToggleFAQ,
  onSubmitForm,
  onScheduleCall,
  onContactSales
}) => {
  switch (activeTab) {
    case 'overview':
      return <OverviewTab />;
    case 'audience':
      return <AudienceTab />;
    case 'pricing':
      return <PricingTab onSelectPlan={onSelectPlan} onContactSales={onContactSales} />;
    case 'faq':
      return <FAQTab activeFAQ={activeFAQ} onToggleFAQ={onToggleFAQ} onContactSales={onContactSales} />;
    case 'contact':
      return (
        <ContactTab 
          formSubmitted={formSubmitted} 
          onSubmitForm={onSubmitForm} 
          onScheduleCall={onScheduleCall} 
        />
      );
    default:
      return <OverviewTab />;
  }
};

// Extracted OverviewTab component
const OverviewTab: React.FC = () => (
  <div className="space-y-16">
    <div className="text-center max-w-3xl mx-auto">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Advertise With TooliQ.ai?</h2>
      
      {/* SEO Content */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 mb-8 border border-gray-200/50">
        <p className="text-gray-700 text-lg leading-relaxed">
          <strong>TooliQ.ai</strong> is the premier destination for AI tool discovery, connecting innovative AI companies 
          with their ideal customers through targeted advertising solutions. Our platform delivers <strong>high-converting traffic</strong> 
          from decision-makers actively evaluating AI tools.
        </p>
      </div>
    </div>

    <div className="grid md:grid-cols-3 gap-8">
      {[
        {
          icon: '🎯',
          title: 'Targeted Audience',
          description: 'Reach developers, businesses, and AI enthusiasts actively searching for solutions.'
        },
        {
          icon: '📈',
          title: 'Proven Results',
          description: 'Our advertisers see average conversion rates of 8-12% from qualified traffic.'
        },
        {
          icon: '💼',
          title: 'Professional Network',
          description: 'Join 200+ AI companies already advertising their tools on our platform.'
        }
      ].map((feature, index) => (
        <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 text-center hover:shadow-md transition-shadow duration-200">
          <div className="text-4xl mb-4">{feature.icon}</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
          <p className="text-gray-600">{feature.description}</p>
        </div>
      ))}
    </div>

    {/* Testimonials */}
    <div>
      <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">Success Stories</h3>
      <div className="grid md:grid-cols-3 gap-8">
        {TESTIMONIALS.map((testimonial, index) => (
          <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="text-4xl mb-4">{testimonial.avatar}</div>
            <p className="text-gray-700 italic mb-4">"{testimonial.quote}"</p>
            <div>
              <div className="font-semibold text-gray-900">{testimonial.author}</div>
              <div className="text-blue-600">{testimonial.company}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Extracted AudienceTab component
const AudienceTab: React.FC = () => (
  <div className="space-y-12">
    <div className="text-center max-w-3xl mx-auto">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Target Audience</h2>
      
      {/* SEO Content */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-8 border border-gray-200/50">
        <p className="text-gray-700 text-lg leading-relaxed">
          Reach a highly engaged audience of <strong>AI professionals, developers, and decision-makers</strong> with 
          purchasing authority for AI tools. Our audience includes <strong>85% technical decision-makers</strong> 
          actively evaluating new AI solutions for their businesses.
        </p>
      </div>
    </div>

    <div className="grid lg:grid-cols-2 gap-12">
      <div className="space-y-6">
        {AUDIENCE_DATA.map((item, index) => (
          <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-semibold text-gray-900">{item.category}</h3>
              <span className="text-blue-600 font-bold">{item.percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-1000"
                style={{ width: `${item.percentage}%` }}
              ></div>
            </div>
            <p className="text-gray-600 mt-3 text-sm">{item.description}</p>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-8 rounded-2xl text-white shadow-xl">
        <h3 className="text-2xl font-bold mb-6">Audience Insights</h3>
        <div className="space-y-4">
          {[
            "85% technical decision-makers",
            "70% have purchasing authority",
            "Average $5,000+ budget for AI tools",
            "45% work at companies with 50+ employees",
            "92% actively evaluating new AI solutions"
          ].map((insight, index) => (
            <div key={index} className="flex items-center">
              <svg className="w-5 h-5 mr-3 text-white/80" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>{insight}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Extracted PricingTab component
interface PricingTabProps {
  onSelectPlan: (planName: string) => void;
  onContactSales: () => void;
}

const PricingTab: React.FC<PricingTabProps> = ({ onSelectPlan, onContactSales }) => (
  <div className="space-y-12">
    <div className="text-center max-w-3xl mx-auto">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Advertising Packages</h2>
      
      {/* SEO Content */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 mb-8 border border-gray-200/50">
        <p className="text-gray-700 text-lg leading-relaxed">
          Choose from our flexible advertising solutions designed for <strong>AI companies of all sizes</strong>. 
          Whether you're a <strong>startup launching a new tool</strong> or an <strong>established company looking to expand your reach</strong>, 
          we have options to match your goals and budget.
        </p>
      </div>
    </div>

    <div className="grid lg:grid-cols-3 gap-8">
      {ADVERTISING_OPTIONS.map((option, index) => (
        <div 
          key={index} 
          className={`relative rounded-2xl p-8 border-2 transition-all hover:shadow-xl hover:-translate-y-1 duration-200 ${
            option.popular 
              ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-white shadow-lg' 
              : 'border-gray-200 bg-white shadow-sm'
          }`}
        >
          {option.popular && (
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium shadow-md">
                Most Popular
              </span>
            </div>
          )}
          
          <h3 className="text-2xl font-bold text-gray-900 mb-3">{option.title}</h3>
          <p className="text-gray-600 mb-6">{option.description}</p>
          
          <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            {option.price}
          </div>
          
          <ul className="space-y-3 mb-8">
            {option.features.map((feature, featureIndex) => (
              <li key={featureIndex} className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
          
          <button 
            onClick={() => onSelectPlan(option.title)}
            className={`w-full py-3 rounded-lg font-semibold transition-colors duration-200 hover:shadow-md ${
              option.popular
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Select Plan
          </button>
        </div>
      ))}
    </div>

    <div className="text-center bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-gray-200/50 shadow-sm">
      <p className="text-gray-700 mb-4">Need a custom advertising solution?</p>
      <button 
        onClick={onContactSales}
        className="text-blue-600 font-semibold hover:text-blue-700 text-lg transition-colors"
      >
        Contact us for enterprise packages →
      </button>
    </div>
  </div>
);

// Extracted FAQTab component
interface FAQTabProps {
  activeFAQ: number | null;
  onToggleFAQ: (index: number) => void;
  onContactSales: () => void;
}

const FAQTab: React.FC<FAQTabProps> = ({ activeFAQ, onToggleFAQ, onContactSales }) => (
  <div className="max-w-4xl mx-auto">
    <div className="text-center mb-12">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-2xl p-6 mb-8 border border-gray-200/50">
        <p className="text-gray-700 text-lg leading-relaxed">
          Get answers to common questions about <strong>advertising on TooliQ.ai</strong>, 
          including <strong>pricing, targeting, and performance tracking</strong>.
        </p>
      </div>
    </div>

    <div className="space-y-4">
      {FAQ_ITEMS.map((faq, index) => (
        <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-sm transition-shadow duration-200">
          <button
            onClick={() => onToggleFAQ(index)}
            className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
          >
            <span className="font-semibold text-gray-900 text-lg">{faq.question}</span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                activeFAQ === index ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {activeFAQ === index && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <p className="text-gray-700">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>

    <div className="text-center mt-12 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-8 border border-gray-200/50 shadow-sm">
      <p className="text-gray-700 mb-4 text-lg">Still have questions?</p>
      <button 
        onClick={onContactSales}
        className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-colors shadow-md hover:shadow-lg"
      >
        Contact Our Team
      </button>
    </div>
  </div>
);

// Extracted ContactTab component
interface ContactTabProps {
  formSubmitted: boolean;
  onSubmitForm: (e: React.FormEvent) => void;
  onScheduleCall: () => void;
}

const ContactTab: React.FC<ContactTabProps> = ({ formSubmitted, onSubmitForm, onScheduleCall }) => (
  <div id="contact-section" className="max-w-4xl mx-auto">
    <div className="text-center mb-12">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Start Advertising Today</h2>
      
      {/* SEO Content */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-8 border border-gray-200/50">
        <p className="text-gray-700 text-lg leading-relaxed">
          Ready to reach <strong>thousands of AI enthusiasts</strong> and <strong>convert them into customers</strong>? 
          Contact our team to discuss your advertising goals and get a personalized strategy.
        </p>
      </div>
    </div>

    {/* Success Message */}
    {formSubmitted && <FormSuccessMessage />}

    <div className="grid lg:grid-cols-2 gap-12">
      <ContactForm onSubmitForm={onSubmitForm} />
      <ContactSidebar onScheduleCall={onScheduleCall} />
    </div>
  </div>
);

// Extracted FormSuccessMessage component
const FormSuccessMessage: React.FC = () => (
  <div id="form-success" className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 shadow-sm">
    <div className="text-center">
      <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
      <p className="text-gray-700">
        Your message has been sent. Our advertising team will contact you within 24 hours to discuss your campaign.
      </p>
    </div>
  </div>
);

// Extracted ContactForm component
interface ContactFormProps {
  onSubmitForm: (e: React.FormEvent) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSubmitForm }) => (
  <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
    <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h3>
    <form onSubmit={onSubmitForm} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
          <input 
            type="text" 
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" 
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
          <input 
            type="text" 
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" 
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
        <input 
          type="email" 
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" 
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
        <input 
          type="text" 
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" 
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
        <textarea 
          rows={4} 
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          placeholder="Tell us about your AI tool and advertising goals..."
        ></textarea>
      </div>
      <button 
        type="submit" 
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors shadow-md hover:shadow-lg"
      >
        Send Message
      </button>
    </form>
  </div>
);

// Extracted ContactSidebar component
interface ContactSidebarProps {
  onScheduleCall: () => void;
}

const ContactSidebar: React.FC<ContactSidebarProps> = ({ onScheduleCall }) => (
  <div className="space-y-8">
    <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
      <h4 className="text-lg font-semibold text-gray-900 mb-3">📞 Contact Information</h4>
      <div className="space-y-2 text-gray-700">
        <div><strong>Email:</strong> advertise@tooliq.ai</div>
        <div><strong>Response Time:</strong> Within 24 hours</div>
        <div><strong>Business Hours:</strong> Mon-Fri, 9AM-6PM EST</div>
      </div>
    </div>

    <div className="bg-green-50 p-6 rounded-xl border border-green-200">
      <h4 className="text-lg font-semibold text-gray-900 mb-3">💼 What to Expect</h4>
      <ul className="space-y-2 text-gray-700">
        <li className="flex items-start">
          <span className="text-green-500 mr-2">✓</span>
          Personalized advertising recommendations
        </li>
        <li className="flex items-start">
          <span className="text-green-500 mr-2">✓</span>
          Audience targeting strategy
        </li>
        <li className="flex items-start">
          <span className="text-green-500 mr-2">✓</span>
          Performance projections
        </li>
        <li className="flex items-start">
          <span className="text-green-500 mr-2">✓</span>
          Custom pricing options
        </li>
      </ul>
    </div>

    <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
      <h4 className="text-lg font-semibold text-gray-900 mb-3">🚀 Quick Start</h4>
      <p className="text-gray-700 mb-3">Ready to get started immediately?</p>
      <button 
        onClick={onScheduleCall}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors shadow-md hover:shadow-lg"
      >
        Schedule a Call
      </button>
    </div>
  </div>
);

// Extracted FinalCTASection component
interface FinalCTASectionProps {
  onContactSales: () => void;
  onScheduleCall: () => void;
}

const FinalCTASection: React.FC<FinalCTASectionProps> = ({ onContactSales, onScheduleCall }) => (
  <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
        Ready to Grow Your AI Business?
      </h2>
      <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
        Join hundreds of successful AI companies already advertising on TooliQ.ai
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button 
          onClick={onContactSales}
          className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl"
        >
          Start Advertising Now
        </button>
        <button 
          onClick={onScheduleCall}
          className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors"
        >
          Schedule a Consultation
        </button>
      </div>
    </div>
  </section>
);

export default React.memo(AdvertisePage);